import { useCallback, useEffect, useState } from 'react';
import { useData } from '../store/data';
import { useSay, useSpeakingKey } from '../hooks/useSay';
import { CardCarousel } from '../components/CardCarousel';
import { SectionMascot } from '../components/Mascot';
import { CardShell } from '../components/CardShell';
import { SoundButton } from '../components/SoundButton';
import { haptic } from '../lib/haptic';
import { playSfx } from '../lib/sfx';
import { ALPHABET, VOWELS, LETTER_ANCHORS, type Letter, type LetterAnchor } from '../lib/alphabet';
import { anchorKey as anchorClipKey } from '../lib/phrases';
import './LetrasView.css';

// Keys de lectura: el nombre y el sonido de la letra resaltan la letra grande
// (es lo que está sonando); el ancla resalta su pastilla; cada sílaba resalta su propio chip.
const letterKey = (upper: string) => `letra:${upper}`;
const anchorKey = (upper: string) => `ancla:${upper}`;
const syllableKey = (combo: string) => `silaba:${combo}`;

export function LetrasView() {
  const { letterSounds } = useData();
  const { say, stop } = useSay();

  const [currentIdx, setCurrentIdx] = useState(0);

  useEffect(() => () => stop(), [stop]);
  useEffect(() => {
    stop();
  }, [currentIdx, stop]);

  const goPrev = useCallback(() => {
    haptic('light');
    playSfx('page');
    setCurrentIdx((prev) => Math.max(0, prev - 1));
  }, []);

  const goNext = useCallback(() => {
    haptic('light');
    playSfx('page');
    setCurrentIdx((prev) => Math.min(ALPHABET.length - 1, prev + 1));
  }, []);

  const current = ALPHABET[currentIdx];

  const playName = useCallback(() => {
    haptic('light');
    say({
      key: letterKey(current.upper),
      text: current.name,
      src: letterSounds[`${current.upper}_nombre`],
    });
  }, [current, letterSounds, say]);

  const playSound = useCallback(() => {
    haptic('light');
    say({
      key: letterKey(current.upper),
      text: current.sound,
      src: letterSounds[`${current.upper}_sonido`],
    });
  }, [current, letterSounds, say]);

  const playAnchor = useCallback(() => {
    haptic('light');
    const anchor = LETTER_ANCHORS[current.lower];
    if (!anchor) return;
    say({
      key: anchorKey(current.upper),
      text: `${current.upper} de ${anchor.word}!`,
      src: letterSounds[anchorClipKey(current.upper)],
    });
  }, [current, say, letterSounds]);

  const playSyllable = useCallback((vowel: string) => {
    haptic('light');
    const combo = current.upper + vowel;
    say({
      key: syllableKey(combo),
      text: current.lower + vowel.toLowerCase(),
      src: letterSounds[combo],
    });
  }, [current, letterSounds, say]);

  return (
    <div className="letras">
      <CardCarousel
        items={ALPHABET}
        getKey={(letter) => letter.upper}
        renderCard={(letter, isActive) => (
          <LetterCard
            letter={letter}
            anchor={LETTER_ANCHORS[letter.lower]}
            isActive={isActive}
            onPlayName={playName}
            onPlaySound={playSound}
            onPlayAnchor={playAnchor}
            onPlaySyllable={playSyllable}
          />
        )}
        currentIdx={currentIdx}
        onIndexChange={setCurrentIdx}
        onPrev={goPrev}
        onNext={goNext}
        prevLabel="Letra anterior"
        nextLabel="Letra siguiente"
        aside={<SectionMascot section="letras" />}
      />
    </div>
  );
}

type CardProps = {
  letter: Letter;
  anchor?: LetterAnchor;
  isActive: boolean;
  onPlayName: () => void;
  onPlaySound: () => void;
  onPlayAnchor: () => void;
  onPlaySyllable: (vowel: string) => void;
};

function LetterCard({
  letter,
  anchor,
  isActive,
  onPlayName,
  onPlaySound,
  onPlayAnchor,
  onPlaySyllable
}: CardProps) {
  const speaking = useSpeakingKey();
  const isSpeakingLetter = speaking === letterKey(letter.upper);
  const isSpeakingAnchor = speaking === anchorKey(letter.upper);

  return (
    <CardShell isActive={isActive} size="spacious">
      <div className={`letter-pair${isSpeakingLetter ? ' is-speaking' : ''}`}>
        <span className="letter-pair-glow" aria-hidden="true" />
        <span className="letter-upper">{letter.upper}</span>
        <span className="letter-lower">{letter.lower}</span>
      </div>

      {anchor && (
        <button
          type="button"
          className={`chip letter-anchor-pill${isSpeakingAnchor ? ' is-speaking' : ''}`}
          onClick={onPlayAnchor}
          aria-label={`${anchor.word}, empieza con ${letter.upper}`}
        >
          <span className="letter-anchor-emoji" aria-hidden="true">{anchor.emoji}</span>
          <span className="letter-anchor-text">
            <span className="letter-anchor-lead">{anchor.word.charAt(0)}</span>
            <span className="letter-anchor-rest">{anchor.word.slice(1)}</span>
          </span>
        </button>
      )}

      <div className="letter-actions">
        <SoundButton icon="play" label="Sonido" color="brand" onClick={onPlaySound} />
        <SoundButton icon="play" label="Nombre" color="subtle" onClick={onPlayName} />
      </div>

      <div className="letter-syllables">
        {VOWELS.map((v) => (
          <button
            key={v}
            type="button"
            className={`btn btn--key letter-syllable${speaking === syllableKey(letter.upper + v) ? ' is-speaking' : ''}`}
            onClick={() => onPlaySyllable(v)}
          >
            <span className="syllable-cons">{letter.upper}</span>
            <span className="syllable-vowel">{v}</span>
          </button>
        ))}
      </div>
    </CardShell>
  );
}
